import { useCallback, useEffect, useState } from "react";
import { ZeroAddress, ZeroHash } from "ethers";

import AxiosFactory from "../services/http.service";
import ApiRoutes from "../config/api.routes";
import { getReadOnlyContract } from "../services/contract.service";
import { SettlementState } from "../types/LandToken";

// Enum order mirrors EcoBank.SettlementState on-chain (Sevin guide §2B/§6):
// NONE, AUCTION_ENDED, KYC_CONFIRMED, DEED_ANCHORED, DEED_ATTESTED, CHALLENGED, FINALISED.
// Only used to decode getSettlement()'s uint8 — the REST view carries the state as a string
// and is unaffected by this ordering.
export const SETTLEMENT_STATE_BY_INDEX: SettlementState[] = [
  "none",
  "auction_ended",
  "kyc_confirmed",
  "deed_anchored",
  "deed_attested",
  "challenged",
  "finalised",
];

// A verification-stage challenge on a live (finalised) easement — the legacy REST shape, which the
// V10.1 backend now projects from chain (SettlementService.legacyTermChallenge).
export interface TermChallenge {
  // Whether the TREE drip is frozen right now. V10.1: `frozen` follows the panel's `inBreach`
  // boolean, not `upheld` — the two are independent (a dismissed challenge can still find the
  // land in breach, and an upheld one can leave the drip running). Nothing thaws it but a real
  // re-verification followed by releaseAfterReVerification. On the chain-fallback path this is
  // read straight from EcoBank.dripFrozen; on the REST path it is the backend's chain read.
  frozen: boolean;
  challengedAt: Date | null;
  challengedBy: string | null;
  // Null while the challenge is still open — this is what the panel acts on.
  judgedAt: Date | null;
  upheld: boolean | null;
  // The panel's finding against the COVENANT (V10.1); null while undetermined or on a
  // backend that predates the field.
  inBreach: boolean | null;
}

// The V10.1 review block GET /settlement/:tokenId serves (`review`), chain-derived server-side:
// the same facts hooks/useCovenantReviewState reads from chain directly, so a page that already
// has the settlement can show WHY a release stopped without a second round of RPC calls. Null
// when the backend's chain read failed (`reviewError`) or the backend predates the field.
export interface SettlementReview {
  dripFrozen: boolean;
  dripFrozenAt: Date | null;
  releasesHalted: boolean;
  unattestedRuns: number;
  unattestedHaltAt: number;
  awaitingBetterRecord: boolean;
  verificationsAtFreeze: number;
  verificationsPerformed: number;
  reVerified: boolean;
  verifier: string | null;
  sponsoringHolder: string | null;
  guardian: string | null;
  // An upheld FIELD finding moved the seat and the dismissed verifier is still seated — the
  // Holder has dismiss → reseat ahead of them. Clears once Core.addVerifierToEasement lands.
  reseatRequired: boolean;
  // A DETERMINED, upheld deed challenge is still holding the settlement.
  heldSettlement: boolean;
  liveChallengeId: number | null;
  lastDeterminedChallengeId: number | null;
}

// Unified settlement view. Timestamps are normalised to Date so callers never have to know
// whether a field arrived as unix seconds (chain) or an ISO string (REST).
export interface Settlement {
  state: SettlementState;

  // ── Chain-only: the REST view does not carry these at all. ──
  // null until a winner is recorded / a deed is anchored, or when the chain read failed.
  winner: string | null;
  escrowAmount: bigint | null;
  attestorCount: number | null;

  // ── Available from either source. ──
  kycConfirmedAt: Date | null;
  deedHash: string | null;
  deedDate: Date | null;
  withholdUntil: Date | null;
  attestedAt: Date | null;

  // ── REST-only. The first four are derived from the backend's own copy of the state, so they
  // are null both on the chain-fallback path and whenever the backend's mirror is behind. ──
  // What has to happen next, who does it, and by when. The deadline comes from server-side
  // window config (V9.1: 2 d activation / 1 h attest-withhold on the accelerated env, 30 d / 3 d
  // in production) — never recompute it client-side, it silently drifts when they retune it.
  nextAction: string | null;
  nextActor: string | null;
  deadlineAt: Date | null;
  // Non-null while a server-signed transaction for this token is in flight — that's
  // "in progress", not "stuck".
  pendingTxAction: string | null;
  // The deed-challenge stamp only; the backend's "challenge upheld at" companion is legacy (served
  // but never written since V10 — the GTA judge lane that stamped it is gone) and is deliberately
  // not mapped. The upheld signal is `review.heldSettlement`, from chain.
  challengedAt: Date | null;
  editionId: number | null;
  // IUCN T-bands, 1–8, lower = more protected. onCovenantTrack is the server's derived
  // "currentBand has come down to targetBand"; null when either band is unset.
  startBand: number | null;
  targetBand: number | null;
  currentBand: number | null;
  onCovenantTrack: boolean | null;
  // Secondary listings and offers revert on-chain until this is true.
  primaryMarketCompleted: boolean | null;
  // The verification-stage challenge on a live covenant (V10.1: EcoChallenge.raiseChallenge; a
  // panel's inBreach finding freezes the patron's TREE drip). The dates and verdict are REST-only —
  // the backend mirrors them off EcoBank's TermChallenged / TermChallengeJudged events — but the
  // freeze itself is a public mapping (EcoBank.dripFrozen), so the chain-fallback path reads it
  // and returns a record with the dates blank rather than hiding the freeze.
  termChallenge: TermChallenge | null;
  // V10.1 review state from the backend's own chain read. Null on the chain-fallback path and on
  // a backend that does not serve it; CovenantChallengePanel reads the chain itself either way.
  review: SettlementReview | null;
  // The wallet that attested the deed — the one EcoBank._requireAttestor accepts for reAttest,
  // which it must call every attestEveryNCycles verifications or ClaimFundNow reverts AttestOverdue.
  // Server-derived from the deed_attested audit row and null before FINALISED; `_attestors` is
  // private on-chain and getSettlement returns only a count.
  attestor: string | null;

  // Which source produced `state`. "rest" means the chain read failed and we are running on the
  // backend's mirror alone.
  source: "rest" | "chain";
}

const toDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  // Chain reads give unix seconds (0 = not reached yet); REST gives an ISO string.
  if (typeof value === "number" || typeof value === "bigint") {
    const n = Number(value);
    return n > 0 ? new Date(n * 1000) : null;
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// GET /settlement/:tokenId — the full record plus the server's derived nextAction / nextActor /
// deadlineAt. 404s for a token the backend's event queue hasn't indexed yet (it runs ~60s
// behind the chain), which is exactly when the chain fallback earns its keep.
const fetchRestSettlement = (tokenId: number | string) => {
  const axios = new AxiosFactory().axios;
  return axios.get(ApiRoutes.settlement.byToken(tokenId)).then((res) => res.data);
};

const fetchChainSettlement = (tokenId: number | string) => {
  const bank = getReadOnlyContract("ecofutures-bank");
  if (!bank) {
    return Promise.reject(new Error("Bank contract not found"));
  }
  return bank.getSettlement(tokenId);
};

// EcoBank.dripFrozen is public (V10.1) — the one term-challenge fact the chain can answer without
// the mirror. Read alongside getSettlement so a REST outage never hides a freeze.
const fetchChainDripFrozen = (tokenId: number | string): Promise<boolean> => {
  const bank = getReadOnlyContract("ecofutures-bank");
  if (!bank) {
    return Promise.reject(new Error("Bank contract not found"));
  }
  return bank.dripFrozen(tokenId).then(Boolean);
};

export interface SettlementRead {
  // null means the read failed outright — a settlement that simply hasn't opened comes back
  // as a real record in state "none", not as null.
  settlement: Settlement | null;
  error: string | null;
}

// One settlement read. Exported as a plain function as well as through the hook below, because
// a caller that has to have the answer *before* it renders (LandPage folds this into its page
// payload so the panel paints on the first frame) can't wait on an effect to fire.
//
// REST for everything the chain can't tell us, the chain for the state itself.
//
// The REST view is where `nextAction` / `deadlineAt` / `pendingTxAction` and the DB-only fields
// (bands, primaryMarketCompleted, editionId) come from — the settlement windows live in backend
// config and no contract exposes them. But the backend's event queue runs ~60s behind the chain,
// so taking `state` from REST would leave the panel showing "awaiting attestation", Attest
// button and all, for a minute after the user's own attestDeed confirmed. The chain read is
// therefore authoritative for state and for the timestamps getSettlement carries, and REST takes
// over wholesale only when the chain read fails.
//
// When the two disagree the backend simply hasn't caught up, so its *derived* fields describe a
// state we have already left and are dropped until it does — the panel falls back to its own
// per-state copy rather than rendering a stale next step. Guide §9's `?onChain=true` drift check
// is the server-side view of this same gap.
//
// Both requests go out together — one round-trip of latency, not two.
export const fetchSettlement = async (tokenId: number | string): Promise<SettlementRead> => {
  const [rest, chain, frozenRead] = await Promise.allSettled([
    fetchRestSettlement(tokenId),
    fetchChainSettlement(tokenId),
    fetchChainDripFrozen(tokenId),
  ]);

  const chainData = chain.status === "fulfilled" ? chain.value : null;
  const chainFrozen: boolean | null = frozenRead.status === "fulfilled" ? frozenRead.value : null;
  const chainOnly = {
    winner: chainData && chainData.winner !== ZeroAddress ? chainData.winner : null,
    escrowAmount: chainData ? (chainData.escrowAmount as bigint) : null,
    attestorCount: chainData ? Number(chainData.attestorCount) : null,
  };

  if (rest.status === "fulfilled" && rest.value) {
    const v = rest.value;
    const restState = (v.settlementState as SettlementState) ?? "none";
    const chainState = chainData ? SETTLEMENT_STATE_BY_INDEX[Number(chainData.state)] ?? "none" : null;
    // The chain wins on disagreement; the backend is behind, never ahead.
    const state = chainState ?? restState;
    const restInSync = restState === state;

    return {
      settlement: {
        state,
        ...chainOnly,
        // getSettlement carries these too, and is the fresher copy when we have it.
        kycConfirmedAt: chainData ? toDate(Number(chainData.kycConfirmedAt)) : toDate(v.kycConfirmedAt),
        deedHash: chainData ? (chainData.deedHash === ZeroHash ? null : chainData.deedHash) : v.deedHash ?? null,
        deedDate: chainData ? toDate(Number(chainData.deedDate)) : toDate(v.deedDate),
        withholdUntil: chainData ? toDate(Number(chainData.withholdUntil)) : toDate(v.withholdUntil),
        attestedAt: chainData ? toDate(Number(chainData.attestedAt)) : toDate(v.attestedAt),
        // Derived from the backend's own state — meaningless once we know it is stale.
        nextAction: restInSync ? v.nextAction ?? null : null,
        nextActor: restInSync ? v.nextActor ?? null : null,
        deadlineAt: restInSync ? toDate(v.deadlineAt) : null,
        pendingTxAction: restInSync ? v.pendingTxAction ?? null : null,
        // State-independent, so they stay useful even while the mirror lags. (The "challenge
        // upheld at" key is on the wire but always null — legacy, never written since V10 — and
        // is ignored.)
        challengedAt: toDate(v.challengedAt),
        editionId: v.editionId ?? null,
        startBand: v.startBand ?? null,
        targetBand: v.targetBand ?? null,
        currentBand: v.currentBand ?? null,
        onCovenantTrack: v.onCovenantTrack ?? null,
        primaryMarketCompleted: v.primaryMarketCompleted ?? null,
        // Deliberately NOT gated on restInSync. The other REST-derived fields describe the step
        // the backend thinks we are on and are dropped while it lags, but a frozen drip is a fact
        // about the token that outlives the state machine — blanking it during the ~60s the mirror
        // is behind would hide the challenge exactly when someone is acting on it.
        termChallenge: v.termChallenge
          ? {
              // The freeze itself is the chain's to answer when it did (the mirror is ~60s behind).
              frozen: chainFrozen ?? !!v.termChallenge.frozen,
              challengedAt: toDate(v.termChallenge.challengedAt),
              challengedBy: v.termChallenge.challengedBy ?? null,
              judgedAt: toDate(v.termChallenge.judgedAt),
              upheld: v.termChallenge.upheld ?? null,
              inBreach: v.termChallenge.inBreach ?? null,
            }
          : null,
        review: v.review
          ? {
              dripFrozen: chainFrozen ?? !!v.review.dripFrozen,
              dripFrozenAt: toDate(v.review.dripFrozenAt),
              releasesHalted: !!v.review.releasesHalted,
              unattestedRuns: Number(v.review.unattestedRuns ?? 0),
              unattestedHaltAt: Number(v.review.unattestedHaltAt ?? 3),
              awaitingBetterRecord: !!v.review.awaitingBetterRecord,
              verificationsAtFreeze: Number(v.review.verificationsAtFreeze ?? 0),
              verificationsPerformed: Number(v.review.verificationsPerformed ?? 0),
              reVerified: !!v.review.reVerified,
              verifier: v.review.verifier ?? null,
              sponsoringHolder: v.review.sponsoringHolder ?? null,
              guardian: v.review.guardian ?? null,
              reseatRequired: !!v.review.reseatRequired,
              heldSettlement: !!v.review.heldSettlement,
              liveChallengeId: v.review.challenge?.challengeId ?? null,
              lastDeterminedChallengeId: v.review.lastDetermined?.challengeId ?? null,
            }
          : null,
        attestor: v.attestor ?? null,
        source: chainState ? "chain" : "rest",
      },
      error: null,
    };
  }

  if (chainData) {
    return {
      settlement: {
        state: SETTLEMENT_STATE_BY_INDEX[Number(chainData.state)] ?? "none",
        ...chainOnly,
        kycConfirmedAt: toDate(Number(chainData.kycConfirmedAt)),
        deedHash: chainData.deedHash === ZeroHash ? null : chainData.deedHash,
        deedDate: toDate(Number(chainData.deedDate)),
        withholdUntil: toDate(Number(chainData.withholdUntil)),
        attestedAt: toDate(Number(chainData.attestedAt)),
        // Derived server-side only — the chain exposes no getter for the window config.
        nextAction: null,
        nextActor: null,
        deadlineAt: null,
        pendingTxAction: null,
        challengedAt: null,
        editionId: null,
        startBand: null,
        targetBand: null,
        currentBand: null,
        onCovenantTrack: null,
        primaryMarketCompleted: null,
        // The freeze is public (EcoBank.dripFrozen) so it is answered here; the dates, verdict
        // and challenger are mirror-only and stay blank. `_attestors` is private, so the attestor
        // genuinely cannot be read and the reAttest button hides itself on this path.
        termChallenge: chainFrozen
          ? { frozen: true, challengedAt: null, challengedBy: null, judgedAt: null, upheld: null, inBreach: null }
          : null,
        review: null,
        attestor: null,
        source: "chain",
      },
      error: null,
    };
  }

  // Both sources failed — report the chain's reason, which is the more diagnostic of the
  // two (a REST 404 just means "not indexed yet" and is expected).
  const reason =
    chain.status === "rejected"
      ? (chain.reason as Error)?.message
      : rest.status === "rejected"
      ? (rest.reason as Error)?.message
      : undefined;
  return { settlement: null, error: reason ?? "Failed to read settlement state" };
};

// Hook form, for callers that are happy to fetch after mounting. Call refresh() after any
// settlement action lands; there is deliberately no polling loop.
const useSettlement = (tokenId: number | string | null | undefined) => {
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (tokenId === null || tokenId === undefined || tokenId === "") {
      setSettlement(null);
      return;
    }
    setLoading(true);
    setError(null);

    const read = await fetchSettlement(tokenId);
    // A failed read leaves the last good answer in place rather than blanking the panel.
    if (read.settlement) {
      setSettlement(read.settlement);
    }
    setError(read.error);
    setLoading(false);
  }, [tokenId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { settlement, loading, error, refresh };
};

export default useSettlement;
